import React, { useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Tooltip } from '@material-ui/core';
import { Checkbox, Form, IconClickable, Text, TextArea, TimePicker, View, Select } from 'src/components/common';
import { IRootState } from 'src/redux/rootReducer';
import './styles.scss';
import { AddDailyTimesheetsPayload, DailyTimesheet, JobItem } from 'src/redux/timesheet/types';
import { CustomModal } from 'src/components/Modal';
import moment from 'moment';
import { isFirstWeekDate, isLastWeekDate } from 'src/utils/momentUtils';
import DataTable from './DataTable';
import { Formik } from 'formik';
import { ErrorService, Yup } from 'src/services';
import { DEFAULT_START_TIME, getDailyTimesheetPayload, isInValidJob, isValidHours } from '../helpers';
import { addDailyTimesheetsAsync, setSelectedDailyTimesheet } from 'src/redux/timesheet/actions';
import { isEmpty } from 'src/validations';

const DailyTimesheetModal: React.FC<Props> = ({
  dailyTableData: dailyData,
  loading,
  commentOptions,
  weeklyTableData,
  openModalType,
  onClose,
  onSave,
  onUpdateDailyTimesheet,
  onSetSelectedDailyData,
}) => {
  const formRef = useRef(null);

  // ====================== Handlers ======================
  const validateSchema = Yup.object().shape({
    startTime: Yup.string()
      .nullable()
      .when(['isNotWorking'], {
        is: isNotWorking => {
          return !isNotWorking;
        },
        then: Yup.string().typeError(ErrorService.MESSAGES.required).required(),
      }),
    comments: Yup.array()
      .test('comment test create', ErrorService.MESSAGES.commentNotWorked, value => {
        const comments = formRef.current?.values?.comments || [];
        const isNotWorking = formRef.current?.values?.isNotWorking || false;
        if (isNotWorking && comments.length === 0) return false;
        return true;
      })
      .test('comment test edit', ErrorService.MESSAGES.commentSubmittedTimesheet, value => {
        const comments = formRef.current?.values?.comments || [];

        if (weeklyTableData?.status === 'submitted' && comments.length === 0) return false;
        return true;
      }),
    commentOther: Yup.string()
      .max(255, ErrorService.MESSAGES.commentMaxLength)
      .trim()
      .test('comment test create', ErrorService.MESSAGES.commentOtherRequired, value => {
        const comments = formRef.current?.values?.comments || [];
        if (comments.includes('Other (provide more info)') && isEmpty(value)) return false;
        return true;
      }),
    jobs: Yup.mixed().when(['isNotWorking', 'startTime'], {
      is: isNotWorking => {
        return !isNotWorking;
      },
      then: Yup.mixed()
        .required(ErrorService.MESSAGES.inValidDailyJobs)
        .test('daily-jobs', ErrorService.MESSAGES.inValidDailyJobs, value => {
          if (!value) return true;

          if (value.some(e => isInValidJob(e))) return false;
          return true;
        })
        .test('daily-jobs-hours', ErrorService.MESSAGES.inValidDailyJobsHour, value => {
          if (!value) return false;
          const isInvalidHours = value.some((e: JobItem) => !isValidHours(e.hours));
          return !isInvalidHours;
        })
        .test('daily-jobs-hours', ErrorService.MESSAGES.tooManyWorkHours, value => {
          if (!value) return false;
          const startTime = formRef.current?.values?.startTime || null;
          const lunchTime = formRef.current?.values?.isNoLunch ? 0 : 0.5;
          let maximumHours = 24;
          let totalHours = lunchTime;
          if (startTime) {
            var endTime = moment(startTime).add(1, 'day').startOf('day');
            var duration = moment.duration(endTime.diff(startTime)).asHours();
            maximumHours = Math.round(duration * 10) / 10;
          }
          value.forEach(j => {
            totalHours += Number(j.hours);
          });
          return 0 < totalHours && totalHours <= maximumHours;
        }),
    }),
  });

  const handleOnPreviousDate = (currentDate, setFieldValue) => {
    const prevDate = moment(currentDate).add(-1, 'day').toISOString();
    setFieldValue('date', prevDate);
    handleLoadDailyData(prevDate);
  };

  const handleOnNextDate = (currentDate, setFieldValue) => {
    const nextDate = moment(currentDate).add(1, 'day').toISOString();
    setFieldValue('date', nextDate);
    handleLoadDailyData(nextDate);
  };

  const handleLoadDailyData = (date: string) => {
    const selectedDate = weeklyTableData.dailyTimes.find(res => res.date === date);
    onSetSelectedDailyData(selectedDate);
  };

  const handleCheckboxChange = (e, values, setFieldValue) => {
    const name = e.target.name;
    const value = e.target.checked;
    setFieldValue(name, value);
    if (name === 'isNoLunch') {
      handleEndTimeChange({ ...values, isNoLunch: value }, setFieldValue);
    }
  };

  const handleDataTableChange = (name, value, values: DailyTimesheet, setFieldValue, setFieldTouched) => {
    setFieldTouched('jobs', true);
    var total: number = 0;
    value.forEach(job => {
      total += Number(job.hours);
    });

    setFieldValue(name, value);
    setFieldValue('totalHours', total);
    if (name === 'jobs') {
      handleEndTimeChange({ ...values, totalHours: total }, setFieldValue);
    }
  };
  const handleSubmit = (values: DailyTimesheet) => {
    if (values.comments[0] !== 'Other (provide more info)') {
      values.commentOther = '';
    }

    if (!!weeklyTableData.id) {
      const payload: AddDailyTimesheetsPayload = {
        id: weeklyTableData.id,
        dailyTimesheets: getDailyTimesheetPayload([values]),
      };
      onUpdateDailyTimesheet(payload);
    } else {
      onSave(values);
    }
  };

  const handleEndTimeChange = (values: DailyTimesheet, setFieldValue) => {
    if (!!values.startTime) {
      let endTime = moment(values.startTime).add(values.totalHours, 'hour');
      if (values.isNoLunch === false) {
        endTime = endTime.add(30, 'minute');
      }
      const maxEndTime = moment().add(1, 'day').startOf('day');
      if (endTime.isAfter(maxEndTime)) {
        endTime = maxEndTime;
      }
      setFieldValue('endTime', endTime.toDate());
    } else {
      setFieldValue('endTime', null);
    }
  };
  const INITIAL_DATA = {
    ...dailyData,
    startTime: dailyData.startTime ?? DEFAULT_START_TIME,
  };

  const renderBody = useMemo(() => {
    const { employeeInfo } = weeklyTableData;
    return (
      <Formik
        key={`modal-table-${dailyData.date}`}
        initialValues={INITIAL_DATA}
        validationSchema={validateSchema}
        onSubmit={handleSubmit}
        innerRef={formRef}>
        {({ values, errors, touched, handleSubmit, getFieldProps, setFieldValue, setFieldTouched }) => {
          const { date } = values;
          const isDirty = formRef.current?.dirty ?? false;
          if (values.comments[0] === '' && weeklyTableData.status === 'submitted') values.comments = [];

          return (
            <Form onSubmit={handleSubmit} autoComplete="off" className="ctn-uam__form">
              <View className="ctn-timesheet-daily">
                <View className="ctn-timesheet-daily__header mb-24" isRow align="center">
                  <Text size={24}>
                    <b>Daily Timesheet:</b>
                    <span>{` • ${employeeInfo.employeeId} - ${employeeInfo.name}  • `}</span>
                  </Text>
                  <View isRow align="center">
                    {!isFirstWeekDate(date) && (
                      <Tooltip title="Previous Date">
                        <IconClickable
                          name="ic_chevron-left"
                          className={'ctn-timesheet__change-week-icon mr-8'}
                          onClick={() => handleOnPreviousDate(values.date, setFieldValue)}
                        />
                      </Tooltip>
                    )}
                    <Text>{`${moment(date).format('ddd, MM/DD')}`}</Text>
                    {!isLastWeekDate(date) && (
                      <Tooltip title="Next Date">
                        <IconClickable
                          name="ic_chevron-right"
                          className={'ctn-timesheet__change-week-icon ml-8'}
                          onClick={() => handleOnNextDate(values.date, setFieldValue)}
                        />
                      </Tooltip>
                    )}
                  </View>

                  <Checkbox.Item
                    className="mb-16 mt-16 ml-16"
                    name="isNotWorking"
                    checked={values.isNotWorking}
                    label="Not worked today"
                    onChange={e => handleCheckboxChange(e, values, setFieldValue)}
                  />
                </View>
                <View className="ctn-timesheet-daily__container">
                  <View className="ctn-timesheet-daily__sidebar">
                    {!values.isNotWorking && (
                      <TimePicker
                        key={`${dailyData.date}-startDate`}
                        selected={values.startTime}
                        containerClassName="ctn-timesheet-daily__sidebar-time-picker"
                        label="Start Time"
                        onBlur={e => setFieldTouched('startTime', true)}
                        onChange={value => {
                          setFieldValue('startTime', value);
                          handleEndTimeChange({ ...values, startTime: value }, setFieldValue);
                        }}
                        errorMessage={touched.startTime ? (errors.startTime as string) : ''}
                      />
                    )}
                    {!values.isNotWorking && (
                      <Checkbox.Item
                        name="isNoLunch"
                        onChange={e => handleCheckboxChange(e, values, setFieldValue)}
                        checked={values.isNoLunch}
                        key={`${dailyData.date}-lunch`}
                        className="ctn-timesheet-daily__sidebar-lunch"
                        label="No Lunch"
                      />
                    )}
                    {!values.isNotWorking && (
                      <TimePicker
                        selected={values.endTime as Date}
                        key={`${dailyData.date}-endTime`}
                        containerClassName="ctn-timesheet-daily__sidebar-time-picker"
                        onChange={() => {}}
                        label="End Time"
                        disabled
                      />
                    )}
                    <View className="ctn-timesheet-daily__sidebar-comment" key={`${dailyData.date}-comments`}>
                      <View className="comment-group">
                        <Select
                          isSearchable={false}
                          creatable={false}
                          label={'Comment'}
                          value={values.comments[0] || ''}
                          options={commentOptions}
                          placeholder="Select"
                          name="comment"
                          onBlur={setFieldTouched}
                          onChange={(_name: string, value) => {
                            setFieldValue('comments', [value]);
                            setFieldTouched('comments', isEmpty(value));
                            setFieldTouched('commentOther', true);
                          }}
                        />

                        {values.comments.includes('Other (provide more info)') && (
                          <TextArea
                            containerClassName="pl-15 ctn-timesheet-daily__sidebar-comment-other"
                            key={`${dailyData.date}-commentOther`}
                            placeholder={'Provide more information...'}
                            errorMessage={touched.commentOther && isDirty ? (errors.commentOther as string) : ''}
                            {...getFieldProps('commentOther')}
                          />
                        )}
                      </View>
                      {touched.comments && isDirty && <p className="ctn-timesheet-daily__error">{errors.comments}</p>}
                    </View>
                  </View>
                  <DataTable
                    dailyData={values}
                    onValueChange={(name, value) =>
                      handleDataTableChange(name, value, values, setFieldValue, setFieldTouched)
                    }
                    onCancel={onClose}
                    errors={errors}
                    isShowError={touched.jobs ? true : false}
                    isDirty={isDirty}
                  />
                </View>
              </View>
            </Form>
          );
        }}
      </Formik>
    );
    // eslint-disable-next-line
  }, [dailyData, loading, openModalType]);

  return (
    <CustomModal openModalType={openModalType} body={renderBody} containerClassName="ctn-timesheet-daily__modal" />
  );
};

type Props = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps & { onClose: (...args: any[]) => void; onSave: (value: DailyTimesheet) => void };

const mapStateToProps = (state: IRootState) => ({
  user: state.auth.authUser,
  weeklyTableData: state.timesheet.weeklyTableData,
  dailyTableData: state.timesheet.dailyTableData,
  commentOptions: state.timesheet.commentOptions,
  loading: state.timesheet.isLoading,
  openModalType: state.timesheet.openModalType,
});

const mapDispatchToProps = {
  onUpdateDailyTimesheet: addDailyTimesheetsAsync.request,
  onSetSelectedDailyData: setSelectedDailyTimesheet,
};

export default connect(mapStateToProps, mapDispatchToProps)(DailyTimesheetModal);
